import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Truck, Plus, Download, Search, Filter, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_META = {
  Draft:     { bg: 'bg-slate-100 text-slate-700 border-slate-200',       label: 'Draft' },
  Issued:    { bg: 'bg-blue-100 text-blue-800 border-blue-200',          label: 'Issued' },
  PickedUp:  { bg: 'bg-amber-100 text-amber-800 border-amber-200',       label: 'Picked Up' },
  Delivered: { bg: 'bg-emerald-100 text-emerald-800 border-emerald-200', label: 'Delivered' },
  Closed:    { bg: 'bg-slate-900 text-white border-slate-900',           label: 'Closed' },
  Canceled:  { bg: 'bg-red-100 text-red-800 border-red-200',             label: 'Canceled' },
};

const BillsOfLadingPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [bols, setBols] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (customerFilter !== 'all') params.customer_id = customerFilter;
      const qs = new URLSearchParams(params).toString();
      const [b, c] = await Promise.all([
        api().get(`/order-bills-of-lading${qs ? `?${qs}` : ''}`),
        api().get('/customers'),
      ]);
      setBols(b.data || []);
      setCustomers(c.data || []);
    } catch { toast.error('Failed to load bills of lading'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, customerFilter]);

  const bulkCsv = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (customerFilter !== 'all') params.customer_id = customerFilter;
      const qs = new URLSearchParams(params).toString();
      const res = await api().get(`/order-bills-of-lading/export.csv${qs ? `?${qs}` : ''}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `bills-of-lading-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Bulk CRM export downloaded');
    } catch { toast.error('Bulk export failed'); }
  };

  const filtered = bols.filter(b => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (b.bol_number || '').toLowerCase().includes(q)
      || (b.customer_name_snapshot || '').toLowerCase().includes(q)
      || (b.packing_slip_number_snapshot || '').toLowerCase().includes(q)
      || (b.tracking_or_pro_number || '').toLowerCase().includes(q)
      || (b.carrier || '').toLowerCase().includes(q);
  });

  const stats = bols.reduce((acc, s) => { acc[s.status] = (acc[s.status] || 0) + 1; return acc; }, {});

  return (
    <div className="space-y-4" data-testid="bills-of-lading-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2"><Truck className="w-7 h-7 text-slate-700" /> Bills of Lading</h1>
          <p className="text-sm text-slate-500 mt-1">Carrier handoff + chain of custody. Issue → Picked Up → Delivered → Close. Spawn from a Packing Slip or start standalone.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={bulkCsv} className="rounded-sm" data-testid="bol-bulk-csv-btn">
            <Download className="w-4 h-4 mr-1" /> Bulk CRM Export
          </Button>
          <Button onClick={() => navigate('/orders/bol/new')} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="bol-create-btn">
            <Plus className="w-4 h-4 mr-1" /> New Bill of Lading
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-6 gap-2">
        {['Draft', 'Issued', 'PickedUp', 'Delivered', 'Closed', 'Canceled'].map(k => {
          const m = STATUS_META[k];
          return (
            <Card key={k} className="industrial-card">
              <CardContent className="p-3 text-center">
                <div className="text-2xl font-bold" data-testid={`bol-stat-${k.toLowerCase()}`}>{stats[k] || 0}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">{m.label}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="text-lg">All Bills of Lading ({filtered.length})</CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative w-56">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search BOL#, customer, PRO…" className="pl-8 rounded-sm" data-testid="bol-search" />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40 rounded-sm" data-testid="bol-status-filter"><Filter className="w-3.5 h-3.5 mr-1" /><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {Object.keys(STATUS_META).map(k => <SelectItem key={k} value={k}>{STATUS_META[k].label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={customerFilter} onValueChange={setCustomerFilter}>
                <SelectTrigger className="w-48 rounded-sm" data-testid="bol-customer-filter"><SelectValue placeholder="Customer…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All customers</SelectItem>
                  {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.customer_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-sm text-slate-500">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              <Truck className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-semibold">No Bills of Lading yet</p>
              <p className="text-xs mt-1">Spawn one from a <Link to="/orders/packing" className="underline hover:text-slate-900">Packing Slip</Link>, or start standalone.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>BOL #</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Linked PS</TableHead>
                  <TableHead>Ship Date</TableHead>
                  <TableHead>Carrier</TableHead>
                  <TableHead>Tracking / PRO</TableHead>
                  <TableHead className="text-right">Pieces</TableHead>
                  <TableHead className="text-right">Weight</TableHead>
                  <TableHead>Hazmat</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(b => {
                  const m = STATUS_META[b.status] || STATUS_META.Draft;
                  return (
                    <TableRow key={b.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/orders/bol/${b.id}`)} data-testid={`bol-row-${b.bol_number}`}>
                      <TableCell className="font-semibold mono">{b.bol_number}</TableCell>
                      <TableCell>{b.customer_name_snapshot}</TableCell>
                      <TableCell className="text-xs mono">{b.packing_slip_number_snapshot || <span className="text-slate-400">standalone</span>}</TableCell>
                      <TableCell className="text-xs">{b.ship_date}</TableCell>
                      <TableCell className="text-xs">{b.carrier || <span className="text-slate-400">—</span>}</TableCell>
                      <TableCell className="text-xs mono">{b.tracking_or_pro_number || <span className="text-slate-400">—</span>}</TableCell>
                      <TableCell className="text-right text-xs mono">{b.total_pieces || 0}</TableCell>
                      <TableCell className="text-right text-xs mono">{Number(b.total_weight_lbs || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                      <TableCell>{b.has_hazmat ? <Badge className="bg-red-100 text-red-800 border-red-200 border rounded-sm text-xs flex items-center gap-1 w-fit"><AlertTriangle className="w-3 h-3" /> HAZ</Badge> : <span className="text-slate-400 text-xs">—</span>}</TableCell>
                      <TableCell><Badge className={`${m.bg} border rounded-sm text-xs`}>{m.label}</Badge></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BillsOfLadingPage;
